/**
 * schematic_dealers.ts — SCHEMATIC DEALERS (2026-09-10): the versonas who
 * sell the designs complex crafting needs.
 *
 * Deterministic like the wilds' work terminals: every CITY and TOWN has one
 * dealer (a smith by preference, else a merchant — the lowest id, so the
 * same face keeps the stall) and a SEASONAL board of offers — a pure
 * function of (siteSeed, tier, epoch), so the client shows exactly what the
 * server will sell and no table has to replicate the board. Epoch = 6 h,
 * the mission-board cadence. Buying pays the DEALER's own wallet (the
 * bazaar-stall bridge — the individual gets paid, PLATFORM absorbs the
 * credits) and learns the design permanently (player_schematic).
 *
 * CLIENT MIRROR of server/src/lib/schematic_dealers.ts (tables + math byte-equivalent).
 */
import { GATED_SCHEMATICS, SCHEMATIC_BY_KEY, schematicPriceSov } from './schematic_catalog.js';

export const DEALER_EPOCH_S = 6 * 3600;
/** A dealer trades within this radius of the settlement center (the
 *  sell-to-town radius — one law for every square-side trade). */
export const DEALER_RANGE_M = 400;
/** Offers per season by settlement tier; hamlets have no dealer. */
export const DEALER_OFFERS: Record<string, number> = { city: 6, town: 4, hamlet: 0, capital: 6, district: 0, circuit: 0 }; // capital wave: a district is never a settlement; the capital's board opens with its residents (phase 4)

export interface DealerOffer {
  schematicKey: string;
  name: string;
  complexity: number;
  priceSov: bigint;
  slot: number;
}

function h01(seed: number | string, salt: string): number {
  let h = 0x811c9dc5;
  const s = `${seed}|${salt}`;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193); }
  return (h >>> 0) / 0xffffffff;
}

export function dealerEpoch(nowMs: number): number {
  return Math.floor(nowMs / (DEALER_EPOCH_S * 1000));
}

/** The seasonal board of one settlement: N distinct gated designs, priced
 *  by complexity with a ±15% per-offer jitter. Slots are stable within an
 *  epoch; the next epoch re-rolls. */
export function dealerOffersFor(siteSeed: number, tier: string, epoch: number): DealerOffer[] {
  const n = DEALER_OFFERS[tier] ?? 0;
  const pool = GATED_SCHEMATICS.map((s) => s.key);
  const out: DealerOffer[] = [];
  const taken = new Set<string>();
  for (let slot = 0; slot < n && taken.size < pool.length; slot++) {
    let idx = Math.floor(h01(siteSeed, `sd${epoch}:${slot}`) * pool.length) % pool.length;
    while (taken.has(pool[idx])) idx = (idx + 1) % pool.length;
    const key = pool[idx];
    taken.add(key);
    const sch = SCHEMATIC_BY_KEY[key];
    const jitter = 0.85 + h01(siteSeed, `sp${epoch}:${slot}`) * 0.3;
    out.push({
      schematicKey: key, name: sch.name, complexity: sch.complexity,
      priceSov: BigInt(Math.max(50, Math.round(schematicPriceSov(sch) * jitter / 10) * 10)),
      slot,
    });
  }
  return out;
}

/** The dealer of a settlement from its agent roster: the smith with the
 *  lowest id, else the merchant with the lowest id, else nobody. Pure over
 *  the rows so client and server agree on the face behind the stall. */
export function pickDealer(agents: Array<{ versonaId: bigint; job: string }>): bigint | null {
  let smith: bigint | null = null, merchant: bigint | null = null;
  for (const a of agents) {
    if (a.job === 'smith' && (smith === null || a.versonaId < smith)) smith = a.versonaId;
    else if (a.job === 'merchant' && (merchant === null || a.versonaId < merchant)) merchant = a.versonaId;
  }
  return smith ?? merchant;
}
